/**
 * Patrimonio mes a mes (puro). Port de Domain_NetWorth.js adaptado al libro V4:
 * liquidez = Σ (saldo inicial + movimientos hasta el día) × propiedad; inversiones = valor de la foto
 * o, en meses pasados sin foto, el capital aportado (estimación).
 */
import { monthEnd, monthRange, prevMonth, type IsoDate, type Month } from './dates.js';
import { round2 } from './money.js';

export interface CashAccount { name: string; share: number; inNetWorth: boolean; opening: number }
export interface CashMovement { account: string; date: IsoDate; amount: number }
export interface Flow { date: IsoDate; amount: number }
export interface Snapshot { month: Month; cash: number; investments: number; source: string }

export function cashAt(accounts: CashAccount[], movements: CashMovement[], date: IsoDate): number {
  const sums = new Map<string, number>();
  for (const m of movements) if (m.date && m.date <= date) sums.set(m.account, (sums.get(m.account) ?? 0) + m.amount);
  let total = 0;
  for (const a of accounts) if (a.inNetWorth) total += (a.opening + (sums.get(a.name) ?? 0)) * (a.share || 1);
  return round2(total);
}

export function investedUntil(flows: Flow[], date: IsoDate): number {
  return round2(flows.filter(f => f.date && f.date <= date).reduce((s, f) => s + f.amount, 0));
}

export function contributionsIn(flows: Flow[], month: Month): number {
  return round2(flows.filter(f => f.date && f.date.slice(0, 7) === month).reduce((s, f) => s + f.amount, 0));
}

/** Fotos que faltan: meses cerrados sin foto. El último, si se toma en los primeros días, usa el valor actual. */
export function planSnapshots(p: { accounts: CashAccount[]; movements: CashMovement[]; flows: Flow[]; snapshots: Snapshot[];
  currentInvestments: number | null; currentMonth: Month; firstMonth: Month; todayDay: number; graceDays?: number }): Snapshot[] {
  const existing = new Set(p.snapshots.map(s => s.month));
  const lastClosed = prevMonth(p.currentMonth);
  if (!p.firstMonth || p.firstMonth > lastClosed) return [];
  return monthRange(p.firstMonth, lastClosed).filter(m => !existing.has(m)).map(m => {
    const end = monthEnd(m);
    const useCurrent = m === lastClosed && p.todayDay <= (p.graceDays ?? 10) && p.currentInvestments !== null;
    return { month: m, cash: cashAt(p.accounts, p.movements, end),
      investments: round2(useCurrent ? p.currentInvestments! : investedUntil(p.flows, end)), source: useCurrent ? 'auto' : 'estimated' };
  });
}

export interface NetWorthPoint { month: Month; cash: number; investments: number; netWorth: number; change: number | null;
  performance: number | null; savings: number | null; estimated: boolean; live: boolean }

/**
 * Serie mensual con cambio, ahorro (Δ liquidez + aportaciones − ajustes de saldo) y rendimiento (Δ inversiones − aportaciones).
 * @param adjustments ajustes de saldo (ya multiplicados por la parte que es tuya): corrigen la liquidez pero no son ahorro.
 */
export function netWorthSeries(snapshots: Snapshot[], live: { month: Month; cash: number; investments: number } | null, flows: Flow[], adjustments: Flow[] = []): NetWorthPoint[] {
  const rows = snapshots.filter(s => /^\d{4}-\d{2}$/.test(s.month) && (!live || s.month !== live.month))
    .map(s => ({ month: s.month, cash: s.cash, investments: s.investments, source: s.source, live: false }));
  if (live) rows.push({ ...live, source: 'live', live: true });
  rows.sort((a, b) => a.month.localeCompare(b.month));
  return rows.map((r, i) => {
    const netWorth = round2(r.cash + r.investments);
    const prev = rows[i - 1];
    const change = prev ? round2(netWorth - round2(prev.cash + prev.investments)) : null;
    // Ahorro = lo que entra en liquidez + lo que pasas a inversiones; se sabe siempre que haya mes anterior.
    // Rendimiento = lo que suben las inversiones sin contar aportaciones; solo entre dos meses con valor real
    // (en un mes estimado no se sabe, y del último estimado al primero real saldría la ganancia acumulada de meses).
    const contributed = contributionsIn(flows, r.month);
    const savings = prev ? round2(r.cash - prev.cash + contributed - contributionsIn(adjustments, r.month)) : null;
    const performance = prev && prev.source !== 'estimated' && r.source !== 'estimated' ? round2(r.investments - prev.investments - contributed) : null;
    return { month: r.month, cash: r.cash, investments: r.investments, netWorth, change, performance, savings, estimated: r.source === 'estimated', live: r.live };
  });
}
